import { BottomCallout } from "@/components/BottomCallout";
import { Button } from "@/components/Button";
import { MultiSelectRadio } from "@/components/MultiSelectRadio";
import { ProgressBar } from "@/components/ProgressBar";
import { SelectInput } from "@/components/SelectInput";
import { TopCallout } from "@/components/TopCallout";
import { faHeart, faLightbulb, faSun, faCloudSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useMemo } from "react";
import { IEPStatus, TelehealthPreference, AvailabilitySchedule, DayOfWeek, TimeSlot, PrimarySupportArea } from "../types";
import { RegistrationFormData } from "../context/registration-context";
import classNames from "classnames";

interface PersonalQuestionsFormProps {
  onContinue: () => void;
  isFromLogin?: boolean;
  saving?: boolean;
  formData: RegistrationFormData;
  updateFormData: (data: Partial<RegistrationFormData>) => void;
}

export const PersonalQuestionsForm: React.FC<PersonalQuestionsFormProps> = ({
  onContinue,
  saving = false,
  formData,
  updateFormData
}) => {
  const [error, setError] = useState<string>("");
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDirtyFields(prev => new Set([...prev, name]));
    
    // Convert string values back to appropriate types
    if (name === "isInSchoolDaycare" || name === "hasHadABAServices") {
      updateFormData({ [name]: value === "true" });
    } else if (name === "hasIEP") {
      updateFormData({ [name]: value === "true" ? IEPStatus.YES : value === "false" ? IEPStatus.NO : value });
    } else if (name === "openToTelehealth") {
      updateFormData({ [name]: value === "true" ? TelehealthPreference.YES : value === "false" ? TelehealthPreference.NO : value });
    } else {
      updateFormData({ [name]: value });
    }
  };

  const handleSupportAreaChange = (selectedValues: string[]) => {
    setDirtyFields(prev => new Set([...prev, 'primarySupportAreas']));
    updateFormData({ primarySupportAreas: selectedValues as PrimarySupportArea[] });
  };

  // Helper function to get time slots for a specific day from the new format
  const getTimeSlotsForDay = (schedule: AvailabilitySchedule | undefined, day: DayOfWeek): TimeSlot[] => {
    if (!schedule?.schedule) return [];
    const daySchedule = schedule.schedule.find(d => d.day === day);
    return daySchedule?.timeSlots || [];
  };

  // Get all validation errors (frontend + backend)
  const getValidationErrors = (): string[] => {
    const validationErrors: string[] = [];
    
    // Frontend validation errors (only for dirty fields)
    if (dirtyFields.has('isInSchoolDaycare') && formData.isInSchoolDaycare === undefined) {
      validationErrors.push("Please select whether your child is enrolled in school or daycare");
    }
    
    if (dirtyFields.has('hasHadABAServices') && formData.hasHadABAServices === undefined) {
      validationErrors.push("Please select whether you have had ABA services before");
    }
    
    if (dirtyFields.has('primarySupportAreas') && (!formData.primarySupportAreas || formData.primarySupportAreas.length === 0)) {
      validationErrors.push("Please select at least one primary support area");
    }
    
    if (dirtyFields.has('openToTelehealth') && formData.openToTelehealth === undefined) {
      validationErrors.push("Please select your telehealth preference");
    }
    
    // IEP validation (only if child is in school/daycare)
    if (formData.isInSchoolDaycare === true && dirtyFields.has('hasIEP') && (formData.hasIEP === undefined || formData.hasIEP === null)) {
      validationErrors.push("Please select whether your child has an IEP");
    }
    
    // Availability schedule validation - check if at least one day has available time slots
    if (dirtyFields.has('availabilitySchedule')) {
      const hasAnyAvailableTimeSlots = formData.availabilitySchedule?.schedule?.some(
        d => d.timeSlots && d.timeSlots.length > 0 && !d.timeSlots.includes(TimeSlot.NOT_AVAILABLE)
      );
      
      if (!hasAnyAvailableTimeSlots) {
        validationErrors.push("Please select at least one time slot for your availability");
      }
    }
    
    // Backend errors (always show)
    if (error) {
      validationErrors.push(error);
    }
    
    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setError("");
      onContinue();
    } catch (error: any) {
      setError(error?.response?.data?.message || "Something went wrong");
    }
  };

  const disabled = useMemo(() => {
    const baseValidation =
      formData.isInSchoolDaycare === undefined ||
      formData.hasHadABAServices === undefined ||
      !formData.primarySupportAreas ||
      formData.primarySupportAreas.length === 0 ||
      formData.openToTelehealth === undefined;

    // Only require IEP validation if child is in school/daycare
    const iepValidation =
      formData.isInSchoolDaycare === true
        ? formData.hasIEP === undefined || formData.hasIEP === null
        : false;

    // Validate that at least one day has been selected with available time slots
    const availabilityValidation = !formData.availabilitySchedule?.schedule || 
      formData.availabilitySchedule.schedule.length === 0 || 
      formData.availabilitySchedule.schedule.every(d => d.timeSlots.length === 0 || d.timeSlots.includes(TimeSlot.NOT_AVAILABLE));

    return baseValidation || iepValidation || availabilityValidation;
  }, [formData]);

  const supportAreas = [
    { value: PrimarySupportArea.COMMUNICATION, label: "Communication" },
    { value: PrimarySupportArea.SEVERE_BEHAVIOR, label: "Severe Behavior" },
    { value: PrimarySupportArea.MINOR_BEHAVIOR_CONCERNS, label: "Minor Behavior Concerns" },
    { value: PrimarySupportArea.SOCIAL_SKILLS, label: "Social Skills" },
    { value: PrimarySupportArea.UNKNOWN, label: "Unknown" }
  ];

  const daysOfWeek = [
    { value: DayOfWeek.MONDAY, label: "Monday" },
    { value: DayOfWeek.TUESDAY, label: "Tuesday" },
    { value: DayOfWeek.WEDNESDAY, label: "Wednesday" },
    { value: DayOfWeek.THURSDAY, label: "Thursday" },
    { value: DayOfWeek.FRIDAY, label: "Friday" },
    { value: DayOfWeek.SATURDAY, label: "Saturday" },
    { value: DayOfWeek.SUNDAY, label: "Sunday" }
  ];

  const timeSlotOptions = [
    { value: TimeSlot.MORNING, label: "Morning", time: "8AM-12PM", icon: faSun },
    { value: TimeSlot.AFTERNOON, label: "Afternoon", time: "12PM-4PM", icon: faCloudSun },
    { value: TimeSlot.EVENING, label: "Evening", time: "4PM-8PM", icon: faMoon }
  ];

  // Helper function to update time slots for a specific day in the new format
  const updateTimeSlotsForDay = (schedule: AvailabilitySchedule | undefined, day: DayOfWeek, timeSlots: TimeSlot[]): AvailabilitySchedule => {
    const currentSchedule = schedule?.schedule || [];
    const existingDayIndex = currentSchedule.findIndex(d => d.day === day);
    
    if (existingDayIndex >= 0) {
      // Update existing day
      const updatedSchedule = [...currentSchedule];
      updatedSchedule[existingDayIndex] = { day, timeSlots };
      return { schedule: updatedSchedule };
    } else {
      // Add new day
      return { schedule: [...currentSchedule, { day, timeSlots }] };
    }
  };

  return (
    <>
      <TopCallout icon={faHeart} title="About You">
        These questions help us better understand your family's needs and get your care started without the long wait. You're in the right place!
      </TopCallout>

      <div className="bg-white p-6 rounded-[16px] shadow-lg">
        <ProgressBar currentStep={2} totalSteps={5} />

        <form className="mt-4 flex flex-col gap-[22px]" onSubmit={handleSubmit}>
          {/* School/Daycare Question */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-black/80 font-medium">
              Is your child currently enrolled in school or daycare?{" "}
              <span className="text-red-500">*</span>
            </label>
            <SelectInput
              data={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
              name="isInSchoolDaycare"
              value={
                formData.isInSchoolDaycare === true
                  ? "true"
                  : formData.isInSchoolDaycare === false
                  ? "false"
                  : ""
              }
              disabledOptionText="Select an option"
              onChange={handleChange}
            />
          </div>

          {/* IEP Question - Only show if child is in school/daycare */}
          {formData.isInSchoolDaycare === true && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-black/80 font-medium">
                Does your child have an Individualized Education Program (IEP)?{" "}
                <span className="text-red-500">*</span>
              </label>
              <SelectInput
                data={[
                  { value: IEPStatus.YES, label: "Yes" },
                  { value: IEPStatus.NO, label: "No" },
                  {
                    value: IEPStatus.IN_PROCESS,
                    label: "In the process of getting one",
                  },
                ]}
                name="hasIEP"
                value={
                  formData.hasIEP === IEPStatus.YES
                    ? IEPStatus.YES
                    : formData.hasIEP === IEPStatus.NO
                    ? IEPStatus.NO
                    : formData.hasIEP || ""
                }
                disabledOptionText="Select an option"
                onChange={handleChange}
              />
            </div>
          )}

          {/* Previous ABA Services */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-black/80 font-medium">
              Have you ever had ABA services before?{" "}
              <span className="text-red-500">*</span>
            </label>
            <SelectInput
              data={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
              name="hasHadABAServices"
              value={
                formData.hasHadABAServices === true
                  ? "true"
                  : formData.hasHadABAServices === false
                  ? "false"
                  : ""
              }
              disabledOptionText="Select an option"
              onChange={handleChange}
            />
          </div>

          {/* Primary Support Areas */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-black/80 font-medium">
              What are your primary areas in need of support? Please select all
              that apply. <span className="text-red-500">*</span>
            </label>
            <MultiSelectRadio
              options={supportAreas}
              value={formData.primarySupportAreas || []}
              onChange={handleSupportAreaChange}
              name="primarySupportAreas"
              className="mt-2"
            />
          </div>

          {/* Availability Schedule */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-black/80 font-medium">
              What is your family's availability for sessions for the next 6
              months? Please provide as much availability as possible.{" "}
              <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-600">
              For most children, effective ABA therapy takes about 25 hours a
              week, though our clinicians might recommend more or less for your
              child.
            </p>
            <div className="flex flex-col gap-3 mt-4">
              {daysOfWeek.map((day) => {
                const dayTimeSlots = getTimeSlotsForDay(
                  formData.availabilitySchedule,
                  day.value
                );
                const isDaySelected = dayTimeSlots.length > 0 && !dayTimeSlots.includes(TimeSlot.NOT_AVAILABLE);

                return (
                  <div key={day.value} className="flex items-center gap-6">
                    <div className="flex items-center gap-2 w-28">
                      <input
                        type="checkbox"
                        id={`day-${day.value}`}
                        checked={isDaySelected}
                        onChange={(e) => {
                          setDirtyFields(
                            (prev) => new Set([...prev, "availabilitySchedule"])
                          );
                          if (!e.target.checked) {
                            // Set to NOT_AVAILABLE when unchecked
                            const newSchedule = updateTimeSlotsForDay(
                              formData.availabilitySchedule,
                              day.value,
                              [TimeSlot.NOT_AVAILABLE]
                            );
                            updateFormData({
                              availabilitySchedule: newSchedule,
                            });
                          } else {
                            // Initialize with morning slot when checked
                            const newSchedule = updateTimeSlotsForDay(
                              formData.availabilitySchedule,
                              day.value,
                              [TimeSlot.MORNING]
                            );
                            updateFormData({
                              availabilitySchedule: newSchedule,
                            });
                          }
                        }}
                        className="w-5 h-5 text-primary rounded-sm border-gray-300 focus:ring-primary accent-primary"
                      />
                      <label
                        htmlFor={`day-${day.value}`}
                        className="text-sm font-medium text-gray-700"
                      >
                        {day.label}
                      </label>
                    </div>

                    <div className="flex gap-3 flex-1">
                      {timeSlotOptions.map((slot) => {
                        const isSelected = isDaySelected && dayTimeSlots.includes(slot.value as TimeSlot);

                        return (
                          <button
                            key={slot.value}
                            type="button"
                            disabled={!isDaySelected}
                            onClick={() => {
                              if (!isDaySelected) return;
                              
                              let newTimeSlots: TimeSlot[];
                              if (isSelected) {
                                // Remove the time slot
                                newTimeSlots = dayTimeSlots.filter(
                                  (ts) => ts !== slot.value
                                );
                              } else {
                                // Add the time slot
                                newTimeSlots = [
                                  ...dayTimeSlots,
                                  slot.value as TimeSlot,
                                ];
                              }

                              // Don't allow removing all slots - keep at least one
                              if (newTimeSlots.length === 0) {
                                newTimeSlots = [TimeSlot.MORNING];
                              }

                              const newSchedule = updateTimeSlotsForDay(
                                formData.availabilitySchedule,
                                day.value,
                                newTimeSlots
                              );
                              setDirtyFields(
                                (prev) =>
                                  new Set([...prev, "availabilitySchedule"])
                              );
                              updateFormData({
                                availabilitySchedule: newSchedule,
                              });
                            }}
                            className={classNames(
                              "flex flex-col items-center justify-center px-4 py-2 rounded-lg border transition-all flex-1 h-[80px]",
                              !isDaySelected
                                ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed opacity-50"
                                : isSelected
                                ? "border-primary bg-tintPrimary text-primary"
                                : "border-gray-300 bg-white text-gray-500 hover:border-gray-400 cursor-pointer"
                            )}
                          >
                            <FontAwesomeIcon
                              icon={slot.icon}
                              className="text-lg mb-0.5"
                            />
                            <span className="text-xs font-medium">
                              {slot.label}
                            </span>
                            <span className="text-[10px] opacity-75 mt-0.5">
                              {slot.time}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Telehealth Question */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-black/80 font-medium">
              Are you open to exploring telehealth supervision sessions? This
              may allow us to get your care started more quickly.{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="text-xs text-gray-600 mb-2">
              Please note: Quality is never compromised. Direct therapy will
              still occur face-to-face, in-person – only BCBA supervision and
              caregiver training will be conducted virtually.
            </div>
            <SelectInput
              data={[
                {
                  value: TelehealthPreference.YES,
                  label: "Yes, I'm open to telehealth",
                },
                {
                  value: TelehealthPreference.NO,
                  label: "No, I prefer in-person only",
                },
                {
                  value: TelehealthPreference.HYBRID,
                  label: "I'm open to a mix of both",
                },
              ]}
              name="openToTelehealth"
              value={
                formData.openToTelehealth === TelehealthPreference.YES
                  ? TelehealthPreference.YES
                  : formData.openToTelehealth === TelehealthPreference.NO
                  ? TelehealthPreference.NO
                  : formData.openToTelehealth || ""
              }
              disabledOptionText="Select an option"
              onChange={handleChange}
            />
          </div>

          {/* Single error summary box */}
          {getValidationErrors().length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-800 text-sm font-medium mb-2">
                Please fix the following errors:
              </div>
              <ul className="text-red-700 text-sm space-y-1">
                {getValidationErrors().map((error, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3">
            <Button
              type="submit"
              variant="primary"
              className="!rounded-full w-full"
              disabled={disabled || saving}
              loading={saving}
            >
              Continue
            </Button>
          </div>
        </form>
      </div>

      <BottomCallout icon={faLightbulb}>
        Starting is the hardest part. You've already taken a big step.
      </BottomCallout>
    </>
  );
}; 